import { gxlookupApi } from "@/app/apiConfig/services/authAbstractApiLayer";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import AnnualRevenueTarget from "@/app/components/common/actions/dropdowns/AnnualRevenueTarget";
import ChannelType from "@/app/components/common/actions/dropdowns/ChannelType";
import HistoricalData from "@/app/components/common/actions/dropdowns/HistoricalData";
import Industry from "@/app/components/common/actions/dropdowns/Industry";
import PeakRevenueMonths from "@/app/components/common/actions/dropdowns/PeakRevenueMonths";
import PricingType from "@/app/components/common/actions/dropdowns/PricingType";
import RevenueModelType from "@/app/components/common/actions/dropdowns/RevenueModelType";
import SalesModel from "@/app/components/common/actions/dropdowns/SalesModel";
import SlowMonths from "@/app/components/common/actions/dropdowns/SlowMonths";
import Subsector from "@/app/components/common/actions/dropdowns/Subsector";
import Bottleneck from "@/app/components/common/actions/inputs-b/Bottleneck";
import DatePickerInput from "@/app/components/common/actions/inputs-b/DatePickerInput";
import DPOTarget from "@/app/components/common/actions/inputs-b/DPOTarget";
import DSOTarget from "@/app/components/common/actions/inputs-b/DSOTarget";
import Floor from "@/app/components/common/actions/inputs-b/Floor";
import GrossMarginTarget from "@/app/components/common/actions/inputs-b/GrossMarginTarget";
import IndustrySeasonalEvents from "@/app/components/common/actions/inputs-b/IndustrySeasonalEvents";
import InventoryHoldingDays from "@/app/components/common/actions/inputs-b/InventoryHoldingDays";
import RatioValue from "@/app/components/common/actions/inputs-b/RatioValue";
import RegisteredAddress from "@/app/components/common/actions/inputs-b/RegisteredAddress";
import Targetline from "@/app/components/common/actions/inputs-b/Targetline";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { useEffect, useState } from "react";
import type { NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import FinType from "@/app/components/common/actions/dropdowns/FinType";

const LOOKUP_TYPES = [
  "COVENANT_TYPE",
  "PARTNER_TYPE",
  "PRICING_TYPE",
  "REVENUE_MODEL_TYPE",
  "CHANNEL_TYPE",
  "SALES_MODEL",
] as const;

type LookupType = (typeof LOOKUP_TYPES)[number];

interface LookupOption {
  gxLookupId: number;
  gxLookupType: string;
  gxLookupCode: string;
  gxLookupValue: string;
  gxLookupDesc?: string;
  gxLookupSeq?: number;
}

interface DropdownOption {
  value: number;
  text: string;
}

const toDropdownOptions = (items: LookupOption[]): DropdownOption[] =>
  (items ?? []).map((item) => ({
    value: item.gxLookupId,
    text: item.gxLookupValue,
  }));

type LookupMap = Record<LookupType, DropdownOption[]>;

const EMPTY_LOOKUPS: LookupMap = {
  COVENANT_TYPE: [],
  PARTNER_TYPE: [],
  PRICING_TYPE: [],
  REVENUE_MODEL_TYPE: [],
  CHANNEL_TYPE: [],
  SALES_MODEL: [],
};

interface CovenantItem {
  id: string;
  FinType: string;
  ratioValue: string;
}

interface PartnerItem {
  id: string;
  partnerName: string;
  partnerType: string;
  autoApprovalThreshold: string;
  autoApprovalThresholdCurrency: string;
  contractExpiryDate: string;
}

interface FinancialProfileForm {
  registeredAddress: string;
  salesModel: string;
  channelType: string;
  industry: string;
  subsector: string;
  revenueModelType: string;
  historicalData: string;
  dsoTarget: string;
  dpoTarget: string;
  inventoryHoldingDays: string;
  annualRevenueTarget: string;
  annualRevenueTargetCurrency: string;
  grossMarginTarget: string;
  monthlyOpexBudget: string;
  monthlyOpexBudgetCurrency: string;
  peakRevenueMonths: string;
  slowMonths: string;
  industrySeasonalEvents: string;
  pricingType: string;
  floor: string;
  autoApprovalThresholdTxn: string;
  autoApprovalThresholdTxnCurrency: string;
  bottleneck: string;
  targetlineAPAging: string;
  autoApprovalThresholdOther: string;
  autoApprovalThresholdOtherCurrency: string;
}

const INITIAL_FORM: FinancialProfileForm = {
  registeredAddress: "",
  salesModel: "",
  channelType: "",
  industry: "",
  subsector: "",
  revenueModelType: "",
  historicalData: "",
  dsoTarget: "",
  dpoTarget: "",
  inventoryHoldingDays: "",
  annualRevenueTarget: "",
  annualRevenueTargetCurrency: "AED",
  grossMarginTarget: "",
  monthlyOpexBudget: "",
  monthlyOpexBudgetCurrency: "AED",
  peakRevenueMonths: "",
  slowMonths: "",
  industrySeasonalEvents: "",
  pricingType: "",
  floor: "",
  autoApprovalThresholdTxn: "",
  autoApprovalThresholdTxnCurrency: "AED",
  bottleneck: "",
  targetlineAPAging: "",
  autoApprovalThresholdOther: "",
  autoApprovalThresholdOtherCurrency: "AED",
};

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const FinancialProfile = () => {
  const [form, setForm] = useState<FinancialProfileForm>(INITIAL_FORM);
  const [lookups, setLookups] = useState<LookupMap>(EMPTY_LOOKUPS);
  const [covenants, setCovenants] = useState<CovenantItem[]>([
    { id: makeId(), FinType: "", ratioValue: "" },
  ]);
  const [partners, setPartners] = useState<PartnerItem[]>([
    {
      id: makeId(),
      partnerName: "",
      partnerType: "",
      autoApprovalThreshold: "",
      autoApprovalThresholdCurrency: "AED",
      contractExpiryDate: "",
    },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setField =
    <K extends keyof FinancialProfileForm>(key: K) =>
    (value: string) =>
      setForm((prev) => ({ ...prev, [key]: value }));

  const setDropdownField =
    <K extends keyof FinancialProfileForm>(key: K) =>
    (val: { value: string }) =>
      setForm((prev) => ({ ...prev, [key]: val?.value ?? "" }));

  const setAmountField =
    <K extends keyof FinancialProfileForm>(key: K) =>
    (event: NumericTextBoxChangeEvent) => {
      const raw = event?.target?.value;
      setForm((prev) => ({
        ...prev,
        [key]: raw === null || raw === undefined ? "" : String(raw),
      }));
    };

  const setCurrencyField =
    <K extends keyof FinancialProfileForm>(key: K) =>
    (code: string) =>
      setForm((prev) => ({ ...prev, [key]: code }));

  const fetchAllLookup = async () => {
    try {
      const results = await Promise.all(
        LOOKUP_TYPES.map((type) => gxlookupApi({ gxLookupType: type })),
      );

      const lookupMap = { ...EMPTY_LOOKUPS };
      LOOKUP_TYPES.forEach((type, idx) => {
        const raw = results[idx];
        const rawList: LookupOption[] = raw?.data ?? raw ?? [];
        lookupMap[type] = toDropdownOptions(rawList);
      });

      setLookups(lookupMap);
    } catch (err) {
      console.error("Error fetching lookups:", err);
    }
  };

  useEffect(() => {
    fetchAllLookup();
  }, []);

  const handleCovenantChange = (
    id: string,
    key: "FinType" | "ratioValue",
    value: string,
  ) => {
    setCovenants((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [key]: value } : c)),
    );
  };

  const addCovenant = () => {
    setCovenants((prev) => [
      ...prev,
      { id: makeId(), FinType: "", ratioValue: "" },
    ]);
  };

  const removeCovenant = (id: string) => {
    setCovenants((prev) =>
      prev.length > 1 ? prev.filter((c) => c.id !== id) : prev,
    );
  };

  const handlePartnerChange = (
    id: string,
    key: keyof Omit<PartnerItem, "id">,
    value: string,
  ) => {
    setPartners((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: value } : p)),
    );
  };

  const addPartner = () => {
    setPartners((prev) => [
      ...prev,
      {
        id: makeId(),
        partnerName: "",
        partnerType: "",
        autoApprovalThreshold: "",
        autoApprovalThresholdCurrency: "AED",
        contractExpiryDate: "",
      },
    ]);
  };

  const removePartner = (id: string) => {
    setPartners((prev) =>
      prev.length > 1 ? prev.filter((p) => p.id !== id) : prev,
    );
  };

  const handleBack = () => {};

  const handleContinue = async () => {
    setSubmitError(null);

    const payload = {
      ...form,
      financialCovenants: covenants.map(({ id, ...rest }) => rest),
      partners: partners.map(({ id, ...rest }) => rest),
    };

    alert(JSON.stringify(payload, null, 2));
    console.log("FinancialProfile submit payload:", payload);

    setSubmitting(true);

    try {
    } catch (err) {
      console.error("Error submitting financial profile:", err);
      setSubmitError("Something went wrong while saving. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gx-smeonboard-card">
      <div className="gx-title-bank">
        <button
          type="button"
          onClick={handleBack}
          className="gx-back-button"
          aria-label="Go back"
        >
          <img src={`${ICON_BASE_PATH}backicon.svg`} alt="backicon" />
        </button>
        <h2 className="gx-h2-l">
          Financial
          <span className="gx-mpin-heading-bold gx-h2-b"> Profile</span>
        </h2>
      </div>
      <p className="gx-p12-m">
        Capture Key business, financial and performance parameters.
      </p>
      <div className="gx-smeonboard-overflow">
        <div className="grid grid-cols-12 gap-4 w-full">
          <div className="col-span-12 mt-5">
            <div className="growx-company-box">
              <div className="gx-helpicon">
                <img src={`${ICON_BASE_PATH}helpicon.svg`} alt="helpicon" />
                <h4 className="gx-h4-sb">Why these parameters?</h4>
              </div>
              <p className="gx-p12-m">
                These parameters help GrowX’s AI Copilot deliver accurate
                insights, anomaly detection and predictive forecasting tailored
                to your business.
              </p>
            </div>
          </div>
          <div className="col-span-12">
            <RegisteredAddress
              label="Registered Address"
              value={form.registeredAddress}
              onChange={setField("registeredAddress")}
            />
          </div>
          <div className="col-span-6">
            <SalesModel
              value={form.salesModel}
              onChange={setDropdownField("salesModel")}
              data={lookups.SALES_MODEL}
            />
          </div>
          <div className="col-span-6">
            <ChannelType
              value={form.channelType}
              onChange={setDropdownField("channelType")}
              data={lookups.CHANNEL_TYPE}
            />
          </div>
          <div className="col-span-6">
            <Industry
              value={form.industry}
              onChange={setDropdownField("industry")}
              data={null}
            />
          </div>
          <div className="col-span-6">
            <Subsector
              value={form.subsector}
              onChange={setDropdownField("subsector")}
              data={null}
            />
          </div>
          <div className="col-span-6">
            <RevenueModelType
              value={form.revenueModelType}
              onChange={setDropdownField("revenueModelType")}
              data={lookups.REVENUE_MODEL_TYPE}
            />
          </div>
          <div className="col-span-6">
            <HistoricalData
              value={form.historicalData}
              onChange={setDropdownField("historicalData")}
              data={null}
            />
          </div>
          <div className="col-span-6">
            <DSOTarget
              label="DSO Target (Days)"
              value={form.dsoTarget}
              onChange={setField("dsoTarget")}
            />
          </div>
          <div className="col-span-6">
            <DPOTarget
              label="DPO Target (Days)"
              value={form.dpoTarget}
              onChange={setField("dpoTarget")}
            />
          </div>
          <div className="col-span-6">
            <InventoryHoldingDays
              label="Inventory Holding Days Target"
              value={form.inventoryHoldingDays}
              onChange={setField("inventoryHoldingDays")}
            />
          </div>
          <div className="col-span-6">
            <AnnualRevenueTarget
              label="Annual Revenue Target"
              value={form.annualRevenueTarget}
              initialCurrencyCode={form.annualRevenueTargetCurrency}
              onChange={setAmountField("annualRevenueTarget")}
              onChangeCurrency={setCurrencyField("annualRevenueTargetCurrency")}
            />
          </div>
          <div className="col-span-6">
            <GrossMarginTarget
              label="Gross Margin Target (%)"
              value={form.grossMarginTarget}
              onChange={setField("grossMarginTarget")}
            />
          </div>
          <div className="col-span-6">
            <AnnualRevenueTarget
              label="Monthly Opex Budget"
              value={form.monthlyOpexBudget}
              initialCurrencyCode={form.monthlyOpexBudgetCurrency}
              onChange={setAmountField("monthlyOpexBudget")}
              onChangeCurrency={setCurrencyField("monthlyOpexBudgetCurrency")}
            />
          </div>
          <div className="col-span-6">
            <PeakRevenueMonths
              value={form.peakRevenueMonths}
              onChange={setDropdownField("peakRevenueMonths")}
              data={null}
            />
          </div>
          <div className="col-span-6">
            <SlowMonths
              value={form.slowMonths}
              onChange={setDropdownField("slowMonths")}
              data={null}
            />
          </div>
          <div className="col-span-6">
            <IndustrySeasonalEvents
              label="Industry Seasonal Events"
              value={form.industrySeasonalEvents}
              onChange={setField("industrySeasonalEvents")}
            />
          </div>
          <div className="col-span-6">
            <PricingType
              value={form.pricingType}
              onChange={setDropdownField("pricingType")}
              data={lookups.PRICING_TYPE}
            />
          </div>
          <div className="col-span-6">
            <Floor
              label="Floor (Minimum Cash Policy)"
              value={form.floor}
              onChange={setField("floor")}
            />
          </div>
          <div className="col-span-6">
            <AnnualRevenueTarget
              label="Auto Approval Threshold for Transactions"
              value={form.autoApprovalThresholdTxn}
              initialCurrencyCode={form.autoApprovalThresholdTxnCurrency}
              onChange={setAmountField("autoApprovalThresholdTxn")}
              onChangeCurrency={setCurrencyField(
                "autoApprovalThresholdTxnCurrency",
              )}
            />
          </div>
          <div className="col-span-6">
            <Bottleneck
              label="Bottleneck (Days)"
              value={form.bottleneck}
              onChange={setField("bottleneck")}
            />
          </div>
          <div className="col-span-6">
            <Targetline
              label="Targetline for AP Aging (Days)"
              value={form.targetlineAPAging}
              onChange={setField("targetlineAPAging")}
            />
          </div>
          <div className="col-span-6">
            <AnnualRevenueTarget
              label="Auto Approval Threshold for Transactions"
              value={form.autoApprovalThresholdOther}
              initialCurrencyCode={form.autoApprovalThresholdOtherCurrency}
              onChange={setAmountField("autoApprovalThresholdOther")}
              onChangeCurrency={setCurrencyField(
                "autoApprovalThresholdOtherCurrency",
              )}
            />
          </div>
          <div className="col-span-12">
            <div className="gx-dialog-title-h3">
              <h3 className="gx-h4-b">Financial Covenant</h3>
            </div>
          </div>
        </div>

        {covenants.map((covenant, index) => (
          <div
            className="grid grid-cols-12 gap-4 items-center w-full"
            key={covenant.id}
          >
            <div className="col-span-1 flex items-center justify-center">
              <label># {index + 1}</label>
            </div>

            <div className="col-span-5">
              <FinType
                value={covenant.FinType}
                onChange={(val: { value: string }) =>
                  handleCovenantChange(
                    covenant.id,
                    "FinType",
                    val?.value ?? "",
                  )
                }
                data={lookups.COVENANT_TYPE}
              />
            </div>

            <div className="col-span-5">
              <RatioValue
                label="Ratio / Value"
                value={covenant.ratioValue}
                onChange={(value: string) =>
                  handleCovenantChange(covenant.id, "ratioValue", value)
                }
              />
            </div>

            <div className="col-span-1 flex items-center justify-center">
              <button
                type="button"
                className="text-red-500 hover:text-red-700 text-sm"
                onClick={() => removeCovenant(covenant.id)}
                disabled={covenants.length === 1}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-12 gap-4 w-full">
          <div className="col-span-12">
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 text-sm"
              onClick={addCovenant}
            >
              Add Covenant
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 w-full">
          <div className="col-span-12 mb-3">
            <div className="flex items-center mt-4">
              <div className="grow border-t border-[#EEEFF2]" />
            </div>
          </div>
          <div className="col-span-12">
            <div className="gx-dialog-title-h3">
              <h3 className="gx-h4-b">Partner Name(s)</h3>
            </div>
          </div>

          {partners.map((partner, index) => (
            <div className="col-span-12" key={partner.id}>
              <div className="grid grid-cols-12 gap-4 w-full items-center">
                <div className="col-span-6">
                  <DPOTarget
                    label={`Partner ${index + 1}`}
                    value={partner.partnerName}
                    onChange={(value: string) =>
                      handlePartnerChange(partner.id, "partnerName", value)
                    }
                  />
                </div>
                <div className="col-span-6">
                  <SlowMonths
                    value={partner.partnerType}
                    onChange={(val: { value: string }) =>
                      handlePartnerChange(
                        partner.id,
                        "partnerType",
                        val?.value ?? "",
                      )
                    }
                    data={lookups.PARTNER_TYPE}
                  />
                </div>
                <div className="col-span-6">
                  <AnnualRevenueTarget
                    label="Auto Approval Threshold for Transactions"
                    value={partner.autoApprovalThreshold}
                    initialCurrencyCode={partner.autoApprovalThresholdCurrency}
                    onChange={(event: NumericTextBoxChangeEvent) =>
                      handlePartnerChange(
                        partner.id,
                        "autoApprovalThreshold",
                        event?.target?.value === null ||
                          event?.target?.value === undefined
                          ? ""
                          : String(event.target.value),
                      )
                    }
                    onChangeCurrency={(code: string) =>
                      handlePartnerChange(
                        partner.id,
                        "autoApprovalThresholdCurrency",
                        code,
                      )
                    }
                  />
                </div>
                <div className="col-span-6">
                  <DatePickerInput
                    label="Partner Contract Expiry / Renewal Date"
                    value={partner.contractExpiryDate}
                    iconSrc={`${ICON_BASE_PATH}date.svg`}
                    onChange={(date: string) =>
                      handlePartnerChange(
                        partner.id,
                        "contractExpiryDate",
                        date,
                      )
                    }
                  />
                </div>
                <div className="col-span-12 flex justify-end">
                  <button
                    type="button"
                    className="text-red-500 hover:text-red-700 text-sm"
                    onClick={() => removePartner(partner.id)}
                    disabled={partners.length === 1}
                  >
                    Remove Partner
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="col-span-12">
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 text-sm"
              onClick={addPartner}
            >
              Add Partner
            </button>
          </div>
        </div>
      </div>

      {submitError && (
        <p className="gx-p12-m text-red-500 mt-2">{submitError}</p>
      )}

      <div className="gx-footer-btn-end">
        <GxPrimaryButton
          label={submitting ? "SAVING..." : "CONTINUE"}
          handleSubmit={handleContinue}
          disabled={submitting}
        />
      </div>
    </div>
  );
};
